import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime'

/**
 * Cache busting for the membership preview remote
 * (docs/MODULE_FEDERATION_GUIDE.md → "Cache busting").
 *
 * The GCS buckets serving the remote attach
 * `Cache-Control: max-age=31536000, immutable` to EVERY object — including
 * `mf-manifest.json` and the fixed-name `remoteEntry.js`. Returning browsers
 * (and Google's shared edge cache) therefore keep resolving a previous remote
 * build after each remote deploy; the previous build's content-hashed chunks
 * are gone, so every editor mount fails with
 * `[MF] Failed to mount membershipPreviewApp/...`.
 *
 * Appending a per-app-boot query param to the manifest URL and to the remote
 * entry script URL guarantees both are fetched fresh once per session, no
 * matter what cache headers the bucket serves. Content-hashed chunks are not
 * touched and keep their long-lived caching.
 */
export const MF_CACHE_BUST_PARAM = 'mfv'

export const createMfCacheBustStamp = (): string => Date.now().toString(36)

export const withMfCacheBust = (url: string, stamp: string): string => {
  if (!stamp || url.includes(`${MF_CACHE_BUST_PARAM}=`)) return url
  return `${url}${url.includes('?') ? '&' : '?'}${MF_CACHE_BUST_PARAM}=${stamp}`
}

const isRemoteEntryScriptUrl = (url: string): boolean => url.includes('remoteEntry')

/**
 * Federation runtime plugin that rewrites the remote entry `<script src>` with
 * the session cache-bust param. Only the entry script goes through this hook —
 * the remote's own webpack runtime loads the exposed content-hashed chunks, so
 * those stay long-term cacheable.
 */
export const createRemoteEntryCacheBustPlugin = (stamp: string): ModuleFederationRuntimePlugin => ({
  name: 'membership-preview-entry-cache-bust',
  createScript({ url, attrs }: { url: string; attrs?: Record<string, unknown> }) {
    if (typeof document === 'undefined' || !isRemoteEntryScriptUrl(url)) return undefined

    const script = document.createElement('script')
    script.src = withMfCacheBust(url, stamp)

    Object.entries(attrs ?? {}).forEach(([name, value]) => {
      if (name === 'async') {
        script.async = Boolean(value)
      } else if (name === 'defer') {
        script.defer = Boolean(value)
      } else if (!script.getAttribute(name)) {
        script.setAttribute(name, String(value))
      }
    })

    return script
  },
})
